import api from "./api";

// ─── Types ───────────────────────────────────────────────────────────────────

interface DaySchedule {
  enabled: boolean; // Whether the user works this day
  from: string; // "HH:mm" format
  to: string; // "HH:mm" format
}

interface WeekSchedule {
  sunday?: DaySchedule;
  monday?: DaySchedule;
  tuesday?: DaySchedule;
  wednesday?: DaySchedule;
  thursday?: DaySchedule;
  friday?: DaySchedule;
  saturday?: DaySchedule;
}

interface TruckYardGeofence {
  id: string;
  name: string;
  latitude?: number;
  longitude?: number;
  radius?: number; // in meters
}

export interface AccessValidationResult {
  allowed: boolean;
  reason?:
    | "outside_geofence"
    | "outside_schedule"
    | "no_yard_assigned"
    | "geolocation_error";
  yardName?: string;
  scheduleInfo?: {
    from: string;
    to: string;
    currentTime: string;
  };
}

// ─── Haversine Distance Calculation ──────────────────────────────────────────

/**
 * Calculate the distance between two geographic coordinates using Haversine formula
 * @returns distance in meters
 */
function calculateHaversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6371000; // Earth's radius in meters
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

// ─── Schedule Validation ─────────────────────────────────────────────────────

/**
 * Get the English day name for current day (Mexico City timezone)
 */
function getCurrentDayName(): keyof WeekSchedule {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Mexico_City",
    weekday: "long",
  });
  const dayName = formatter.format(now);
  // Map English day name to our key
  const dayMap: Record<string, keyof WeekSchedule> = {
    sunday: "sunday",
    monday: "monday",
    tuesday: "tuesday",
    wednesday: "wednesday",
    thursday: "thursday",
    friday: "friday",
    saturday: "saturday",
  };
  return dayMap[dayName.toLowerCase()] || "monday";
}

/**
 * Parse time string "HH:mm" to minutes since midnight
 */
function parseTimeToMinutes(time: string): number {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
}

/**
 * Get current time in "HH:mm" format (Mexico City timezone)
 */
function getCurrentTime(): string {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Mexico_City",
    hour: "2-digit",
    minute: "2-digit",
    // No `hour12: false`: en en-US Chrome y Node dan «24:30» entre 00:00 y 00:59 y el
    // horario 00:00–06:00 negaba el acceso esa hora (mismo caso que yardDay.ts).
    hourCycle: "h23",
  });
  const timeStr = formatter.format(now);
  return timeStr;
}

/**
 * Check if current time is within the user's schedule for today
 */
function isWithinSchedule(schedule: WeekSchedule | undefined): {
  allowed: boolean;
  scheduleInfo?: { from: string; to: string; currentTime: string };
} {
  if (!schedule) {
    // No schedule configured means no restrictions
    return { allowed: true };
  }

  const currentDay = getCurrentDayName();
  const daySchedule = schedule[currentDay];

  // Check if day exists and is enabled
  if (!daySchedule || daySchedule.enabled === false) {
    // Day not configured or disabled means user shouldn't work today
    return {
      allowed: false,
      scheduleInfo: {
        from: "No programado",
        to: "No programado",
        currentTime: getCurrentTime(),
      },
    };
  }

  // Check if from/to are configured
  if (!daySchedule.from || !daySchedule.to) {
    return {
      allowed: false,
      scheduleInfo: {
        from: "No configurado",
        to: "No configurado",
        currentTime: getCurrentTime(),
      },
    };
  }

  const currentTime = getCurrentTime();
  const currentMinutes = parseTimeToMinutes(currentTime);
  const fromMinutes = parseTimeToMinutes(daySchedule.from);
  const toMinutes = parseTimeToMinutes(daySchedule.to);

  // Handle overnight shifts (e.g., 22:00 to 06:00)
  let allowed: boolean;
  if (fromMinutes <= toMinutes) {
    // Normal shift (e.g., 08:00 to 16:00)
    allowed = currentMinutes >= fromMinutes && currentMinutes <= toMinutes;
  } else {
    // Overnight shift (e.g., 22:00 to 06:00)
    allowed = currentMinutes >= fromMinutes || currentMinutes <= toMinutes;
  }

  return {
    allowed,
    scheduleInfo: {
      from: daySchedule.from,
      to: daySchedule.to,
      currentTime,
    },
  };
}

// ─── Get User's Current Position ─────────────────────────────────────────────

/**
 * Request location permission explicitly
 * Returns true if permission is granted, false otherwise
 */
export async function requestLocationPermission(): Promise<boolean> {
  if (!navigator.geolocation) {
    return false;
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      () => resolve(true),
      (error) => {
        console.error("Location permission denied:", error);
        resolve(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  });
}

/**
 * Get user's current geolocation
 */
function getUserPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation not supported"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log("[Geolocation] User position obtained:", {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
        resolve(position);
      },
      reject,
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  });
}

// ─── Fetch Yard Data ─────────────────────────────────────────────────────────

/**
 * Trae los patios pedidos en UNA sola petición.
 *
 * Ojo con el `params: { id }` que había acá: `getYardsList`
 * (truck-yard/truckYardController.js) **nunca lee `req.query.id`**. Devuelve la
 * página 1 — `limit` 20 por omisión, ordenada por `createdAt` descendente — y
 * el cliente hacía `.find()` sobre eso. O sea que el filtro por id era
 * decorativo: cada llamada traía la misma página completa, y un patio fuera de
 * los 20 más nuevos era invisible. Hoy PTAR tiene 7 patios en producción, así
 * que no se nota; al pasar de 20, un supervisor asignado a un patio viejo se
 * habría validado sólo contra los nuevos → `outside_geofence` permanente.
 *
 * Con el supervisor multi-patio de #497 el fan-out por id además multiplicaba
 * la misma petición por patio (7 llamadas paralelas idénticas cada 5 minutos y
 * en cada `visibilitychange`), sin comprar nada.
 *
 * Devuelve `null` si la petición falla, que NO es lo mismo que "no hay patios":
 * quien llama tiene que distinguirlo para no fallar cerrado por un error de red.
 *
 * `complete` dice si la respuesta trajo el catálogo entero (`meta.totalItems`
 * contra lo devuelto). Sólo cuando es `true` un id ausente PRUEBA que el patio
 * no existe; si vino truncada, ausente no significa nada.
 */
export async function fetchYardsByIds(
  yardIds: string[],
  token: string,
): Promise<{ yards: Map<string, TruckYardGeofence>; complete: boolean } | null> {
  try {
    const response = await api.get("/truck-yard/yards", {
      // Alto a propósito: el endpoint pagina de a 20 y no filtra por id, así
      // que la única forma de garantizar que el patio del usuario venga en la
      // respuesta es pedir la lista entera.
      params: { limit: 1000 },
      headers: { Authorization: `Bearer ${token}` },
      // El login llama aquí antes de crear la sesión de backend, así que
      // `validateActiveSession` contesta 403 NO_ACTIVE_SESSION. Es esperado:
      // no debe disparar la alerta de "sesión expirada" ni mandar al login.
      skipSessionExpiredAlert: true,
    });

    const list = response.data?.data || [];
    const totalItems = response.data?.meta?.totalItems;
    const complete =
      typeof totalItems !== "number" || list.length >= totalItems;

    const wanted = new Set(yardIds);
    const yards = new Map<string, TruckYardGeofence>();
    for (const y of list) {
      if (!wanted.has(y.id)) continue;
      yards.set(y.id, {
        id: y.id,
        name: y.name,
        latitude: y.latitude,
        longitude: y.longitude,
        radius: y.radius,
      });
    }
    return { yards, complete };
  } catch (error) {
    console.error("Error fetching yards:", error);
    return null;
  }
}

/** ¿Este patio tiene geocerca configurada? Sin las tres cosas no se puede medir. */
const hasGeofence = (y: TruckYardGeofence) =>
  Boolean(y.latitude && y.longitude && y.radius);

// ─── Main Validation Function ────────────────────────────────────────────────

/**
 * Validate if a guard or supervisor can access the system based on:
 * 1. Geolocation (must be within yard's geofence)
 * 2. Schedule (must be within their work schedule)
 */
export async function validateGuardSupervisorAccess(
  userData: {
    /**
     * Los patios del usuario (issue #497). La geocerca se valida contra
     * CUALQUIERA de ellos, no contra el primero.
     *
     * Tomar `yard_ids[0]` parecía inofensivo porque vigilante e inspector son
     * de un solo patio (`SINGLE_YARD_TYPES` en truck-yard/yardAssignment.js),
     * pero el supervisor también pasa por esta validación —
     * `requiresAccessValidation` en login/functions.ts lo incluye, y
     * useAccessValidation la repite cada 5 minutos — y el supervisor SÍ es
     * multi-patio desde #497. Un supervisor con ["A","B"] parado en B habría
     * medido la distancia contra A: login rechazado con "Fuera del Área de
     * Trabajo" y expulsión al primer tick si ya estaba dentro. Es decir: la
     * feature habría roto la sesión de los usuarios que habilita.
     */
    yard_ids?: string[];
    /** Compatibilidad con documentos sin migrar. Se prefiere `yard_ids`. */
    yard_id?: string;
    schedule?: WeekSchedule;
    /**
     * Se apaga por usuario desde la ficha de personal (Patios > Personal).
     * `undefined` cuenta como activa: los usuarios dados de alta antes del
     * toggle no traen el campo y deben seguir validándose.
     */
    geofence_validation_enabled?: boolean;
  },
  token: string,
): Promise<AccessValidationResult> {
  const geofenceEnabled = userData.geofence_validation_enabled !== false;
  // El plural manda; el escalar solo cubre documentos sin migrar.
  const yardIds =
    userData.yard_ids && userData.yard_ids.length > 0
      ? userData.yard_ids.filter(Boolean)
      : userData.yard_id
        ? [userData.yard_id]
        : [];

  if (yardIds.length === 0) {
    return {
      allowed: false,
      reason: "no_yard_assigned",
    };
  }

  // 1. Horario primero. No depende del patio ni de la geolocalización, así que
  //    se valida sin pedir nada a la API — y sigue aplicando aunque la geocerca
  //    esté apagada: el toggle es solo de ubicación.
  const scheduleResult = isWithinSchedule(userData.schedule);

  if (!scheduleResult.allowed) {
    return {
      allowed: false,
      reason: "outside_schedule",
      scheduleInfo: scheduleResult.scheduleInfo,
    };
  }

  // Con la geocerca apagada no hace falta el patio: nos ahorramos la llamada.
  if (!geofenceEnabled) {
    return { allowed: true };
  }

  // 2. Fetch yard data to get geofence info — una sola petición para todos.
  //
  // De acá en adelante hay TRES situaciones distintas que antes se mezclaban en
  // un solo booleano de "fallo abierto", y mezclarlas costaba caro en los dos
  // sentidos. Esta validación corre en cada login y cada 5 minutos, y su rechazo
  // no es benigno: `useAccessValidation` llama a `logoutUser()`.
  //
  //   a) No se pudo leer el catálogo (red, 500, sesión). TRANSITORIO → abrir.
  //   b) El patio existe pero no tiene geocerca configurada → no puede probar
  //      que el usuario NO está ahí → abrir.
  //   c) Un id del usuario no resuelve nunca (patio borrado, o de otra
  //      compañía). PERMANENTE → se ignora ese id. NO puede abrir, porque
  //      entonces borrar un patio desactivaría la geocerca para siempre de todos
  //      los que lo tenían asignado, en silencio.
  const fetched = await fetchYardsByIds(yardIds, token);

  if (!fetched) {
    // (a) If we can't fetch yard data, allow access (fail open)
    console.warn("Could not fetch yard data, allowing access");
    return { allowed: true };
  }

  const unresolved = yardIds.filter((id) => !fetched.yards.has(id));

  if (unresolved.length > 0 && !fetched.complete) {
    // La respuesta vino truncada, así que "ausente" no prueba "inexistente":
    // el patio donde el usuario está parado puede haber quedado fuera de la
    // página. Indistinguible de (a) → abrir.
    console.warn(
      `[Geofence] Catálogo de patios incompleto y faltan ${unresolved.length} de ${yardIds.length} patios del usuario. Se permite el acceso: no se puede probar que esté afuera.`,
    );
    return { allowed: true };
  }

  if (unresolved.length > 0) {
    // (c) Ids que no existen. Se ignoran: no habilitan ni bloquean nada.
    console.warn(
      `[Geofence] ${unresolved.length} patio(s) asignado(s) al usuario no existen y se ignoran (${unresolved.join(", ")}). Conviene limpiarlos de su ficha.`,
    );
  }

  const yards = [...fetched.yards.values()];

  if (yards.length === 0) {
    // Todos sus ids son fantasma. No queda nada contra qué medir, así que no se
    // puede probar que esté afuera — pero es un dato de ficha roto, no una
    // situación normal.
    console.warn(
      "[Geofence] Ninguno de los patios asignados al usuario existe. Se permite el acceso; hay que corregir su asignación.",
    );
    return { allowed: true };
  }

  // (b) Patios sin geocerca configurada. NO cortan acá.
  //
  // Cortar antes de medir era un atajo demasiado ancho: un solo patio mal
  // configurado apagaba el control sobre todos los demás patios del supervisor,
  // que sí están bien. Ahora se mide primero contra los que tienen geocerca, y
  // el "no puedo desmentirlo" sólo se usa cuando el usuario quedó fuera de
  // TODOS los medibles — que es el único caso en que de verdad es indecidible.
  //
  // Sigue abriendo en ese caso, y no al revés, porque negar dejaría afuera a
  // alguien parado en un patio que la ficha no sabe describir; el código de un
  // solo patio permitía el acceso en esa misma situación. Hoy es una rama
  // defensiva: medido en producción, los 7 patios tienen lat/lon/radio, así que
  // ningún usuario real la toca.
  const unfenced = yards.filter((y) => !hasGeofence(y));
  const fenced = yards.filter(hasGeofence);

  if (fenced.length === 0) {
    console.warn(
      `[Geofence] Ninguno de los patios del usuario tiene geocerca configurada (${unfenced.map((y) => y.name).join(", ")}). No hay nada que validar.`,
    );
    return { allowed: true };
  }

  let position: GeolocationPosition;
  try {
    // Una sola lectura del GPS para todos los patios: pedirla por patio
    // multiplicaría el prompt de ubicación y el consumo en el móvil del
    // supervisor.
    position = await getUserPosition();
  } catch (error) {
    console.error("Geolocation error:", error);
    return {
      allowed: false,
      reason: "geolocation_error",
      yardName: fenced[0].name,
    };
  }

  const userLat = position.coords.latitude;
  const userLon = position.coords.longitude;

  const measured = fenced.map((yard) => {
    const distance = calculateHaversineDistance(
      userLat,
      userLon,
      Number(yard.latitude),
      Number(yard.longitude),
    );
    return { yard, distance, inside: distance <= Number(yard.radius) };
  });

  console.log("[Geofence Debug]", {
    userLat,
    userLon,
    yards: measured.map((m) => ({
      yardName: m.yard.name,
      yardRadius: m.yard.radius,
      distanceMeters: m.distance.toFixed(2),
      isInside: m.inside,
    })),
  });

  if (!measured.some((m) => m.inside)) {
    // Está fuera de todos los patios medibles. Recién ACÁ pesa un patio sin
    // geocerca: podría estar parado en ése, y ninguna medición puede
    // desmentirlo. Fuera de este punto, un patio mal configurado no afecta al
    // control sobre los demás.
    if (unfenced.length > 0) {
      console.warn(
        `[Geofence] Fuera de todos los patios medibles, pero ${unfenced.length} patio(s) del usuario no tienen geocerca configurada (${unfenced.map((y) => y.name).join(", ")}). Se permite el acceso: no se puede probar que esté afuera. Hay que cargarles la geocerca.`,
      );
      return { allowed: true };
    }

    // El patio más cercano es el que se le nombra al usuario: con varios
    // patios, decirle siempre el primero de la lista sería desorientador.
    const nearest = measured.reduce((a, b) => (b.distance < a.distance ? b : a));
    return {
      allowed: false,
      reason: "outside_geofence",
      yardName: nearest.yard.name,
    };
  }

  // All validations passed
  return { allowed: true };
}
